import * as React from 'react';
import { connect } from 'react-redux';
import * as shortid from 'shortid';
import * as moment from 'moment';
import {
  Form,
  IconButton,
  Title,
  Subtitle,
  RouteWrapper,
  Signature,
  Hero,
  Section,
  Notification,
  Box,
} from '../../components';
import {
  verifySignatureAccess,
  storeSignature,
} from '../../redux/actions/signatures';

interface Props {
  dispatch: Function;
}

interface State {
  isCheckingAccess: boolean;
  isVerified: boolean;
  hasConfirmed: boolean;
  hasTickedBox: boolean;
  hasSigned: boolean;
  isSubmitting: boolean;
  signature?: any;
  payload?: any;
  signatureKey: any;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isCheckingAccess: false,
      isVerified: false,
      hasConfirmed: false,
      hasTickedBox: false,
      hasSigned: false,
      isSubmitting: false,
      signatureKey: shortid.generate(),
    };
  }

  renderAccessForm() {
    return (
      <Hero className="is-grey is-fullheight">
        <div className="columns is-centered">
          <div className="column is-one-third">
            <Form
              type="accessCode"
              fields={[
                {
                  name: 'accessCode1',
                  type: 'number',
                  label: 'Booking Code',
                },
                { name: 'accessCode2', type: 'number', label: 'Driver Code' },
              ]}
              disabled={this.state.isCheckingAccess}
              handleSubmit={async ({ accessCode1, accessCode2 }) => {
                this.setState({ isCheckingAccess: true });
                const response = await this.props.dispatch(
                  verifySignatureAccess(`${accessCode1}-${accessCode2}`),
                );
                if (response.error) {
                  this.setState({
                    isCheckingAccess: false,
                  });
                  return;
                }
                this.setState({
                  isCheckingAccess: false,
                  isVerified: true,
                  payload: response.data,
                });
              }}
            />
          </div>
        </div>
      </Hero>
    );
  }

  renderSignatureForm() {
    const payload = this.state.payload;
    return (
      <Section>
        <Title>{payload.contact_name}</Title>
        <Subtitle>
          Please read the following terms & conditions and sign below.
        </Subtitle>
        <Box>{this.renderTermsAndConditions()}</Box>
        {!!this.state.payload.return_date && (
          <Notification type="warning">
            Your van must be returned by <strong>7:00am</strong> on{' '}
            {moment(this.state.payload.return_date)
              .add(1, 'day')
              .format('dddd Do MMMM YYYY')}
          </Notification>
        )}
        <Box>
          {!this.state.hasConfirmed ? (
            <div className="field">
              <p>
                I agree that the condition of the vehicle is as stated on the
                vehicle condition form.
              </p>
              {this.state.payload.pays_own_insurance ? (
                <p>
                  I also confirm my motor insurance will be in force on this
                  vehicle throughout the rental period.
                </p>
              ) : (
                <div className="field">
                  <p>
                    I understand the following are <strong>not</strong> covered
                    by the Collision Damage Waiver and I will be responsible for
                    the full cost of repair:
                  </p>
                  <ol>
                    <li>Overhead and roof damage</li>
                    <li>Incorrect fuel and or fluids</li>
                    <li>Loss of keys</li>
                  </ol>
                  <p>
                    Responsibility amount:{' '}
                    <strong>
                      £{Number(payload.responsibility_amount).toFixed(2)}
                    </strong>
                  </p>
                </div>
              )}
              <div className="buttons is-centered">
                <IconButton
                  icon={this.state.hasTickedBox ? 'check-square-o' : 'square-o'}
                  className={
                    this.state.hasTickedBox ? 'is-primary' : 'is-default'
                  }
                  handleClick={() =>
                    this.setState({ hasTickedBox: !this.state.hasTickedBox })
                  }
                >
                  I agree to all of the above
                </IconButton>
                <IconButton
                  icon="arrow-right"
                  isRight
                  className={`is-${
                    this.state.hasTickedBox ? 'default' : 'static'
                  }`}
                  handleClick={() => {
                    if (!this.state.hasTickedBox) {
                      return;
                    }
                    this.setState({ hasConfirmed: true });
                  }}
                >
                  Continue
                </IconButton>
              </div>
            </div>
          ) : (
            <>
              <div className="field">
                <Signature
                  key={this.state.signatureKey}
                  handleSign={(image) => {
                    this.setState({ hasSigned: true, signature: image });
                  }}
                />
                <p className="has-text-centered">
                  <small className="has-text-grey">
                    Please sign within the dashed lines
                  </small>
                </p>
              </div>
              <div className="field has-text-centered fade-in-up">
                <p>
                  By checking the button below you confirm you have read and
                  understood the above terms and conditions.
                </p>
                <IconButton
                  className={`is-${
                    this.state.hasSigned ? 'primary' : 'static'
                  }`}
                  icon="check"
                  handleClick={async () => {
                    if (!this.state.hasSigned) {
                      return;
                    }
                    if (!this.state.isSubmitting) {
                      this.setState({ isSubmitting: true });
                      const response = await this.props.dispatch(
                        storeSignature({
                          booking_id: payload.booking_id,
                          company_id: payload.company_id,
                          driver_id: payload.driver_id,
                          image: this.state.signature,
                        }),
                      );
                      if (response.error) {
                        this.setState({
                          isSubmitting: false,
                        });
                        return;
                      }
                      this.setState({
                        isCheckingAccess: false,
                        isVerified: false,
                        hasSigned: false,
                        hasConfirmed: false,
                        isSubmitting: false,
                        payload: undefined,
                        signature: undefined,
                      });
                      window.alert(
                        'Thank you for booking with Hirus! ' +
                          'Please return this device back to a member of staff.',
                      );
                    }
                  }}
                >
                  Accept and Submit Signature
                </IconButton>
                &nbsp;
                <IconButton
                  icon="eraser"
                  className="is-default"
                  handleClick={() => {
                    this.setState({
                      hasSigned: false,
                      signature: undefined,
                      signatureKey: shortid.generate(),
                    });
                  }}
                >
                  Clear
                </IconButton>
              </div>
            </>
          )}
        </Box>
      </Section>
    );
  }

  renderTermsAndConditions() {
    if (!this.state.payload) {
      return null;
    }
    return (
      <div dangerouslySetInnerHTML={{ __html: this.state.payload.content }} />
    );
  }

  render() {
    return (
      <RouteWrapper title="Signature Section">
        {this.state.isVerified
          ? this.renderSignatureForm()
          : this.renderAccessForm()}
      </RouteWrapper>
    );
  }
}

export default connect()(Route);
